import React, { useState } from "react";
import AuthLayout from "./AuthLayout";
import { forgetPassword } from "../../lib/auth-client";

interface ForgotPasswordPageProps {
  onNavigate: (page: "login") => void;
}

const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = ({ onNavigate }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email) {
      setError("ERROR: EMAIL REQUIRED");
      return;
    }

    setLoading(true);

    try {
      const result = await forgetPassword({
        email,
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (result.error) {
        setError(`ERROR: ${result.error.message?.toUpperCase() || "REQUEST FAILED"}`);
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setError("ERROR: CONNECTION FAILED. TRY AGAIN.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout title="*** EMAIL SENT ***">
        <div className="space-y-6 text-center">
          <div className="text-green-500 text-6xl mb-4">✓</div>
          <div className="text-green-400 text-sm space-y-2">
            <p>PASSWORD RESET LINK SENT TO:</p>
            <p className="text-green-500 font-bold">{email}</p>
          </div>
          <div className="text-green-600 text-xs space-y-2">
            <p>CHECK YOUR EMAIL FOR INSTRUCTIONS.</p>
            <p>LINK EXPIRES IN 1 HOUR.</p>
          </div>
          <button
            onClick={() => onNavigate("login")}
            className="w-full py-3 bg-green-500 text-black font-bold tracking-widest hover:bg-green-400 transition-colors text-sm"
          >
            [RETURN TO LOGIN]
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="*** PASSWORD RECOVERY ***">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="text-green-600 text-sm mb-4">
          ENTER YOUR EMAIL ADDRESS TO RECEIVE A PASSWORD RESET LINK.
        </div>

        {/* Email */}
        <div>
          <label className="block text-green-500 text-sm mb-2 tracking-wider">
            EMAIL:
          </label>
          <div className="flex items-center border-2 border-green-700 focus-within:border-green-500 bg-black">
            <span className="px-3 text-green-600">{">"}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
              className="flex-1 px-2 py-3 bg-transparent text-green-400 outline-none tracking-wider"
              placeholder="user@domain.com"
              autoFocus
            />
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div
            className="border-2 border-red-500 bg-black p-3 text-center"
            style={{ animation: "shake 0.5s ease-in-out" }}
          >
            <span className="text-red-400 text-sm">{error}</span>
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-green-500 text-black font-bold tracking-widest hover:bg-green-400 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ boxShadow: "0 0 20px rgba(34, 197, 94, 0.4)" }}
        >
          {loading ? "[SENDING...]" : "[SEND RESET LINK]"}
        </button>

        {/* Back to login */}
        <button
          type="button"
          onClick={() => onNavigate("login")}
          className="w-full py-2 text-green-600 hover:text-green-400 text-sm"
        >
          [BACK TO LOGIN]
        </button>
      </form>
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
